// Shared snapshot of Progress Report / Overview data for the product coach.

let reportSnapshot = {
  groups: [],
  projects: [],
  labels: [],
  selectedGroup: null,
  selectedProject: null,
  selectedLabel: null,
  resources: [],
  events: [],
  issuesByDev: null,
  isLoading: false,
};

const listeners = new Set();

export const getReportSnapshot = () => reportSnapshot;

export const setReportSnapshot = (partial = {}) => {
  reportSnapshot = {
    ...reportSnapshot,
    ...partial,
  };
  listeners.forEach((listener) => listener(reportSnapshot));
};

export const subscribeReportSnapshot = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const summarizeIssue = (eventItem = {}) => {
  const title = eventItem.title || 'This issue';
  const author = eventItem.author || 'No Assignee';
  const label = eventItem.label || 'no label';
  const issue = eventItem.issue || '';
  const start = eventItem.start ? String(eventItem.start).slice(0, 10) : 'unknown';
  const end = eventItem.end ? String(eventItem.end).slice(0, 10) : 'unknown';

  return [
    `${issue ? `${issue} ` : ''}${title}`,
    `Assignee: ${author}. Label: ${label}.`,
    `Timeline window in GitDone uses created → updated (${start} → ${end}).`,
    'Drag the bar end to update due date (confirm dialog), or ask me to balance workload.',
  ].join(' ');
};

export const summarizeActivity = ({ activity = [], members = [], issues = [] } = {}) => {
  const memberNames = members.map((m) => m.name).filter(Boolean);
  const filtered = activity.filter((item) => memberNames.includes(item.author));
  const recent = filtered.slice(0, 5);
  const issueCount = Array.isArray(issues) ? issues.length : Object.keys(issues || {}).length;

  if (!recent.length) {
    return [
      `Overview currently shows ${issueCount} issue(s) in session.`,
      'No recent activity matched your tracked members yet.',
      'Open Progress Report to load a project Gantt, or wait for new GitLab commits/issues.',
    ].join(' ');
  }

  const lines = recent.map((item, index) => {
    return `${index + 1}. ${item.title} — ${item.author} (${item.date || 'recent'})`;
  });

  return [
    `Here is a short activity readout for tracked members (${recent.length} shown):`,
    ...lines,
    `Assigned issues in session: ${issueCount}. Ask me to open Progress Report when you want the Gantt.`,
  ].join('\n');
};

export const summarizeGanttLoad = (snapshot = reportSnapshot) => {
  const eventCount = (snapshot.events || []).length;
  const resourceCount = (snapshot.resources || []).length;
  const projectName = snapshot.selectedProject?.label || 'no project';
  const groupName = snapshot.selectedGroup?.label || 'no group';

  if (!snapshot.selectedGroup) {
    return 'Progress Report is empty until you select a group, then a project.';
  }
  if (!snapshot.selectedProject) {
    return `Group “${groupName}” is selected. Next pick a project to load issues onto the Gantt.`;
  }
  if (snapshot.isLoading) {
    return `Loading issues for “${projectName}”…`;
  }
  if (!eventCount) {
    return [
      `“${projectName}” loaded with no issues for your tracked members.`,
      'Try another project, clear the label filter, or confirm member seats include the assignees.',
    ].join(' ');
  }

  const byDev = snapshot.issuesByDev || {};
  const top = Object.keys(byDev)
    .map((name) => ({ name, count: byDev[name] }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 3);

  let workload = '';
  if (top.length) {
    workload = ` Top load: ${top
      .map((item) => `${item.name} (${item.count})`)
      .join(', ')}.`;
  }

  return [
    `Gantt for “${projectName}” in “${groupName}”: ${resourceCount} issue rows, ${eventCount} timeline bars.`,
    workload,
    'Bars use created_at → updated_at from GitLab. Ask me to suggest a label filter or explain a bar.',
  ].join(' ');
};

export const suggestWorkloadMove = (snapshot = reportSnapshot, members = []) => {
  const byDev = snapshot.issuesByDev || {};
  const names = Object.keys(byDev);
  if (names.length < 2) {
    return {
      message:
        'Need at least two assignees with issues loaded to suggest a rebalance.',
      actions: [],
    };
  }

  const ranked = names
    .map((name) => ({ name, count: byDev[name] }))
    .sort((a, b) => b.count - a.count);
  const busiest = ranked[0];
  const lightest = ranked[ranked.length - 1];
  const events = snapshot.events || [];
  const candidate = events.find((item) => item.author === busiest.name);

  if (!candidate) {
    return {
      message: `${busiest.name} looks busiest (${busiest.count}), but I could not pick an issue to move.`,
      actions: [],
    };
  }

  const member = (members || []).find((item) => item.name === lightest.name);
  const assigneeId = member?.id || candidate.authorId;

  return {
    message: [
      `${busiest.name} has ${busiest.count} issues; ${lightest.name} has ${lightest.count}.`,
      `Suggested move: “${candidate.title}” → ${lightest.name}.`,
      'Say “confirm reassign” to write that assignee change to GitLab.',
    ].join(' '),
    actions: [],
    proposal: {
      event: candidate,
      assigneeId,
      assigneeName: lightest.name,
    },
  };
};

export const suggestReportFilters = (snapshot = reportSnapshot) => {
  const actions = [];
  const messages = [];

  if (!snapshot.selectedGroup && (snapshot.groups || []).length) {
    const group = snapshot.groups[0];
    actions.push({ type: 'select_report_group', group });
    messages.push(`I suggest starting with group “${group.label}”.`);
    return { actions, messages };
  }

  if (
    snapshot.selectedGroup &&
    !snapshot.selectedProject &&
    (snapshot.projects || []).length
  ) {
    const project = snapshot.projects[0];
    actions.push({ type: 'select_report_project', project });
    messages.push(`I suggest project “${project.label}” next.`);
    return { actions, messages };
  }

  if (
    snapshot.selectedProject &&
    !snapshot.selectedLabel &&
    (snapshot.labels || []).length
  ) {
    const label = snapshot.labels[0];
    messages.push(
      `Optional: filter by label “${label.label}” if you want a narrower Gantt. Say “filter ${label.label}” to apply it.`,
    );
    return { actions, messages };
  }

  messages.push(summarizeGanttLoad(snapshot));
  return { actions, messages };
};
